import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './app.scss';
import DetailsView from './components/detailsView/DetailsView';
import VulnerabilitiesList from './components/vulnerabilitiesList/VulnerabilitiesList';
import VulnerabilitiesController from './services/VulnerabilitiesController';
import Vulnerability from './types/Vulnerability';

type Props = {
  id: string;
  payloadUrl: string;
};

const App: React.FC<Props> = (props) => {
  // Vulnerabilities service controller.
  const [vulnerabilitiesController] = useState<VulnerabilitiesController>(new VulnerabilitiesController());

  // Local states.
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>();
  const [currentVulnerabilityId, setCurrentVulnerabilityId] = useState<string | null>(null); // UUID.

  // Use navigate and location hooks to create our custom routes without the browser refreshing.
  const navigate = useNavigate();
  const location = useLocation();
  const PATH_NAME = '/vulnerability'; // the URL pathname.

  // Initialise: load the payload.
  useEffect(() => {
    vulnerabilitiesController.fetchPayload(
      props.payloadUrl,
      (vulnerabilitiesList: Vulnerability[]) => {
        // First render of the list.
        setVulnerabilities(vulnerabilitiesList);
        updateLocation();
      },
      (error: Error) => {
        // Show some nice error UI here - use alert() for this test:
        alert(`Oh no, something went wrong: ${error.message}`);
      }
    );
  }, []);

  // Handle when the location or the vulnerabilities list has changed.
  useEffect(() => {
    if (!vulnerabilities) return;

    updateLocation();
  }, [location, vulnerabilities]);

  const updateLocation = () => {
    // Automatically select the first vulnerability in the list if the path and search are empty.
    if (vulnerabilities && location.pathname === '/' && location.search === '') {
      onClickVulnerability(vulnerabilities[0]);
    } else {
      // Ensure the pathname is correct.
      const uuid = location.pathname === PATH_NAME ? new URLSearchParams(location.search).get('uuid') : null;
      setCurrentVulnerabilityId(uuid);
    }
  };

  const onClickVulnerability = (vulnerability: Vulnerability) => {
    // Set the browser's history
    navigate(`${PATH_NAME}?uuid=${vulnerability.uuid}`);
  };

  const renderList = () => {
    if (!vulnerabilities) return null;

    return (
      <VulnerabilitiesList
        id="vulnerabilities-list"
        vulnerabilities={vulnerabilities}
        currentVulnerabilityId={currentVulnerabilityId}
        onClickVulnerability={onClickVulnerability}
      />
    );
  };

  const renderDetails = () => {
    // Get the vulnerability by it's uuid and if not found, don't render anything.
    const vulnerability = vulnerabilitiesController.getVulnerabilityById(currentVulnerabilityId);
    if (!vulnerability) return null;

    return <DetailsView id="details-view" vulnerability={vulnerability} />;
  };

  return vulnerabilities ? (
    <div id={props.id} data-testid={props.id} className={styles['app']}>
      <div id="list" className={styles['list']}>
        {renderList()}
      </div>

      <div id="details" className={styles['details']}>
        {renderDetails()}
      </div>
    </div>
  ) : (
    <div>Loading, please wait...</div>
  );
};

export default App;
